// Canonical option lists for the AERO Maintenance marketplace vertical.
//
// This is the single source of truth shared by the DB defaults, the API server
// (validation + lead request-type gating) and the web app (dropdowns, filters,
// quick-action dialogs). Keeping the lists here keeps every layer in lockstep —
// add a category/capability/request type once and it flows everywhere.
//
// Provider taxonomy fields are stored as plain text / text[] in the DB (like the
// FBO fuel brand), so legacy / free-text values never break; these lists drive
// the pickers and the cross-discipline search behavior.

/**
 * Maintenance disciplines. A provider picks exactly one Primary category and any
 * number of Secondary categories from this same list. Search by a discipline
 * matches the primary category OR a secondary category OR a capability.
 */
export const MAINTENANCE_CATEGORIES = [
  "General Maintenance",
  "Avionics",
  "Paint",
  "Interior",
  "Completions",
  "AOG/Mobile",
  "Parts/Components",
  "Engines/Powerplant",
  "Airframe Repair",
  "Pre-Buy/Inspection",
  "Specialty Repair",
] as const;

export type MaintenanceCategory = (typeof MAINTENANCE_CATEGORIES)[number];

/**
 * Granular skills/services a provider can advertise. Capabilities let a buyer
 * filter beyond the broad discipline (e.g. "ADS-B / NextGen Upgrades").
 */
export const MAINTENANCE_CAPABILITIES = [
  "Scheduled Inspections (Annual/Phase)",
  "Unscheduled / AOG Repair",
  "Avionics Installation",
  "Avionics Repair",
  "ADS-B / NextGen Upgrades",
  "Glass Cockpit Retrofit",
  "Exterior Paint",
  "Interior Refurbishment",
  "Cabinetry / Woodwork",
  "Upholstery",
  "Aircraft Completions",
  "Engine Overhaul",
  "Engine Hot Section",
  "Propeller Service",
  "Sheet Metal / Structural Repair",
  "Composite Repair",
  "Landing Gear Overhaul",
  "Parts Sales",
  "Component Exchange / Rotables",
  "Pre-Purchase Inspection",
  "Borescope Inspection",
  "NDT / Non-Destructive Testing",
  "Weight & Balance",
  "STC / Modifications",
  "Battery Service",
  "Oxygen System Service",
] as const;

export type MaintenanceCapability = (typeof MAINTENANCE_CAPABILITIES)[number];

/** Aircraft categories a provider services. */
export const MAINTENANCE_AIRCRAFT_CATEGORIES = [
  "Jet",
  "Turboprop",
  "Piston",
  "Helicopter",
  "Light Sport",
] as const;

export type MaintenanceAircraftCategory =
  (typeof MAINTENANCE_AIRCRAFT_CATEGORIES)[number];

/** Where the provider performs work. A provider can have more than one mode. */
export const MAINTENANCE_LOCATION_MODES = [
  "On-Airport",
  "Off-Airport",
  "Mobile",
] as const;

export type MaintenanceLocationMode =
  (typeof MAINTENANCE_LOCATION_MODES)[number];

/** Suggested certifications / authorizations (free-text allowed). */
export const MAINTENANCE_CERTIFICATIONS = [
  "FAA Part 145 Repair Station",
  "FAA Part 21 (PMA/STC)",
  "EASA Part 145",
  "IA (Inspection Authorization)",
  "AS9100",
  "ISO 9001",
  "Manufacturer Authorized Service Center",
  "Garmin Authorized Dealer",
  "Collins Aerospace Authorized",
  "Honeywell Authorized",
] as const;

/** Suggested avionics brands (free-text allowed). */
export const MAINTENANCE_AVIONICS_BRANDS = [
  "Garmin",
  "Collins Aerospace",
  "Honeywell",
  "Universal Avionics",
  "Avidyne",
  "Bendix/King",
  "Genesys Aerosystems",
  "L3Harris",
] as const;

/**
 * Quick-action lead/request types surfaced on the provider detail page. A
 * provider enables a subset; only enabled types render their request button.
 */
export interface MaintenanceRequestType {
  /** Stable id stored on the lead and in `enabledRequestTypes`. */
  id: string;
  /** Button + dialog label (Title Case). */
  label: string;
  /** Short helper text shown in the request dialog. */
  description: string;
}

export const MAINTENANCE_REQUEST_TYPES: readonly MaintenanceRequestType[] = [
  {
    id: "maintenance_quote",
    label: "Request Maintenance Quote",
    description: "Get a quote for scheduled or unscheduled maintenance work.",
  },
  {
    id: "aog_support",
    label: "Request AOG Support",
    description: "Aircraft on ground — request urgent mobile or in-shop support.",
  },
  {
    id: "avionics_quote",
    label: "Request Avionics Quote",
    description: "Quote an avionics installation, repair, or upgrade.",
  },
  {
    id: "paint_interior_consultation",
    label: "Request Paint/Interior Consultation",
    description: "Discuss an exterior paint or interior refurbishment project.",
  },
  {
    id: "pre_buy_inspection",
    label: "Request Pre-Buy Inspection",
    description: "Schedule a pre-purchase inspection before you buy.",
  },
  {
    id: "annual_phase_inspection",
    label: "Request Annual/Phase Inspection",
    description: "Book an annual or phase inspection for your aircraft.",
  },
  {
    id: "parts_support",
    label: "Request Parts Support",
    description: "Source parts, components, or rotable exchanges.",
  },
  {
    id: "contact_provider",
    label: "Contact Provider",
    description: "Send a general inquiry to this provider.",
  },
];

/** All request-type ids; the default set enabled for a new/seeded provider. */
export const MAINTENANCE_REQUEST_TYPE_IDS: readonly string[] =
  MAINTENANCE_REQUEST_TYPES.map((t) => t.id);

/** Resolve a stored request-type id to its metadata. */
export function maintenanceRequestType(
  id: string | null | undefined,
): MaintenanceRequestType | undefined {
  if (!id) return undefined;
  return MAINTENANCE_REQUEST_TYPES.find((t) => t.id === id);
}

/** Lead status options for provider/admin triage. */
export const MAINTENANCE_LEAD_STATUSES = [
  "Submitted",
  "In Progress",
  "Quoted",
  "Won",
  "Completed",
  "Cancelled",
] as const;

export type MaintenanceLeadStatus = (typeof MAINTENANCE_LEAD_STATUSES)[number];
